import { useEffect, useRef, useState } from "react";

/**
 * Keeps showing the last non-null `value` after it turns null, until the
 * exit transitions running on `ref`'s element finish, so a panel can animate
 * out before it unmounts. With no transitions (reduced motion, tests) it
 * lets go right away; a new value while leaving cancels the exit.
 */
export const useLingeringValue = <T, E extends HTMLElement = HTMLElement>(
    value: T | null,
) => {
    const [lastValue, setLastValue] = useState(value);
    const ref = useRef<E>(null);
    if (value !== null && value !== lastValue) {
        setLastValue(value);
    }
    const isLeaving = value === null && lastValue !== null;

    useEffect(() => {
        if (!isLeaving) {
            return;
        }
        let isCancelled = false;
        const animations = ref.current?.getAnimations?.() ?? [];
        void Promise.allSettled(
            animations.map((animation) => animation.finished),
        ).then(() => {
            if (!isCancelled) {
                setLastValue(null);
            }
        });
        return () => {
            isCancelled = true;
        };
    }, [isLeaving]);

    return { value: value ?? lastValue, isLeaving, ref };
};
